import { Component, Input, OnInit } from '@angular/core';
import {
  AGRICULTURE_TYPE_LABELS,
  FAILURE_OF_DEDUCTION_COLUMNS,
  FAILURE_OF_DEDUCTION_LIST_KEY,
  FARMING_TYPE_LABELS,
  INCOME_FORM_AGRICULTURE_KEYS,
  IncomeFormAgricultureKey,
  SPECIAL_AGRI_INCOME_TYPE_LABELS,
} from '../../../../constants/income-from-agriculture-constant';

@Component({
    selector: 'app-income-from-agriculture-en-v2',
    templateUrl: './income-from-agriculture-en-v2.component.html',
    styleUrls: [
        './income-from-agriculture-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class IncomeFromAgricultureEnV2Component implements OnInit {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  incomeFormAgricultureData: any[] = [];

  readonly agricultureTypeKey = 'IA_AGRICULTURE_TYPE';
  readonly specialAgriIncomeTypeKey = 'IA_SPECIAL_AGRI_TYPE';
  readonly failureOfDeductionType = 'failure_of_deduction_56';
  readonly failureOfDeductionColumns = FAILURE_OF_DEDUCTION_COLUMNS;
  readonly maintainBooksKey = 'IA_MAINTAIN_BOOKS_OF_ACT';

  private readonly textParticulars = INCOME_FORM_AGRICULTURE_KEYS
    .filter((k) => k.type === 'text')
    .slice()
    .sort((a, b) => a.order - b.order);

  constructor() { }

  getValue(item: any, key: string): any {
    if (!item) {
      return null;
    }
    if (key === this.specialAgriIncomeTypeKey) {
      return item[key] ?? item.specialAgriIncomeType;
    }
    return item[key];
  }

  hasValue(item: any, key: string): boolean {
    const val = this.getValue(item, key);
    if (key === this.maintainBooksKey) {
      return val !== null && val !== undefined && val !== '';
    }
    return val !== null && val !== undefined && val !== '' && val !== '0';
  }

  maintainsBooksOfAccounts(item: any): boolean {
    const val = this.getValue(item, this.maintainBooksKey);
    return val === true || val === 1 || val === '1' || val === 'true' || val === 'Yes' || val === 'YES';
  }

  isApplicableForType(particular: IncomeFormAgricultureKey, agricultureType: string): boolean {
    if (!particular.types || particular.types.length === 0) {
      return true;
    }
    return particular.types.includes(agricultureType);
  }

  getTextParticulars(item: any): IncomeFormAgricultureKey[] {
    const agricultureType = String(this.getValue(item, this.agricultureTypeKey) || '').trim();
    return this.textParticulars.filter((field) =>
      field.key !== this.agricultureTypeKey
      && this.isApplicableForType(field, agricultureType)
      && this.hasValue(item, field.key)
    );
  }

  getAgricultureTypeTitle(item: any): string {
    const value = this.getValue(item, this.agricultureTypeKey);
    const code = value === null || value === undefined ? '' : String(value).trim();
    return AGRICULTURE_TYPE_LABELS[code] || code;
  }

  getTextFieldDisplayValue(item: any, field: IncomeFormAgricultureKey): string {
    const value = this.getValue(item, field.key);
    if (field.key === this.agricultureTypeKey) {
      return AGRICULTURE_TYPE_LABELS[String(value).trim()] || String(value);
    }
    if (field.key === 'IA_FARMING_TYPE') {
      return FARMING_TYPE_LABELS[String(value).trim()] || String(value);
    }
    if (field.key === this.specialAgriIncomeTypeKey) {
      return SPECIAL_AGRI_INCOME_TYPE_LABELS[String(value).trim()] || String(value);
    }
    if (field.key === this.maintainBooksKey) {
      return this.formatYesNo(value);
    }
    if (field.key === 'IA_TOTAL_CULTIVATION_AREA') {
      const unit = this.getValue(item, 'IA_UNIT_OF_CUL_AREA');
      return unit ? `${value} (${unit})` : String(value);
    }
    if (field.key === 'IA_UNIT_OF_CUL_AREA') {
      // Shown inline with cultivation area.
      return '';
    }
    return String(value ?? '');
  }

  shouldShowTextField(item: any, field: IncomeFormAgricultureKey): boolean {
    if (field.key === 'IA_UNIT_OF_CUL_AREA' || field.key === this.agricultureTypeKey) {
      return false;
    }
    return this.hasValue(item, field.key);
  }

  private formatYesNo(value: any): string {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 'Yes' || value === 'YES') {
      return 'Yes';
    }
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'No' || value === 'NO') {
      return 'No';
    }
    return String(value ?? '');
  }

  private getAmountKeysByGroup(item: any, group?: 'balanceSheet'): IncomeFormAgricultureKey[] {
    if (this.isFailureOfDeduction(item)) {
      return [];
    }
    const agricultureType = String(this.getValue(item, this.agricultureTypeKey) || '').trim();
    return INCOME_FORM_AGRICULTURE_KEYS
      .filter((k) =>
        k.type === 'amount'
        && this.isApplicableForType(k, agricultureType)
        && k.group === group
        && this.hasValue(item, k.key)
      )
      .slice()
      .sort((a, b) => a.order - b.order);
  }

  getAmountParticulars(item: any): IncomeFormAgricultureKey[] {
    return this.getAmountKeysByGroup(item, undefined);
  }

  getBalanceSheetParticulars(item: any): IncomeFormAgricultureKey[] {
    if (!this.maintainsBooksOfAccounts(item)) {
      return [];
    }
    return this.getAmountKeysByGroup(item, 'balanceSheet');
  }

  isFailureOfDeduction(item: any): boolean {
    const incomeType = String(this.getValue(item, this.specialAgriIncomeTypeKey) || '').trim();
    return incomeType === this.failureOfDeductionType || incomeType === 'special_agri_any_adjustment';
  }

  getFailureOfDeductionList(item: any): any[] {
    const list = item?.[FAILURE_OF_DEDUCTION_LIST_KEY];
    return Array.isArray(list) ? list : [];
  }

  getFailureOfDeductionRowValue(row: any, key: string): any {
    return row ? row[key] : null;
  }

  getFailureOfDeductionTotal(item: any, field: string): number {
    return this.getFailureOfDeductionList(item).reduce((total, row) => {
      const value = Number(this.getFailureOfDeductionRowValue(row, field));
      return total + (isNaN(value) ? 0 : value);
    }, 0);
  }

  ngOnInit(): void {
    const agricultureData = this.returnData?.incomeData?.agricultureData;
    const parsed = Array.isArray(agricultureData)
      ? agricultureData
      : agricultureData?.Data ?? [];
    this.incomeFormAgricultureData = Array.isArray(parsed) ? parsed : [];
  }
}
